import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Alert, Badge, Button, Form, Spinner } from 'react-bootstrap';
import SettingsModal from '../Settings/SettingsModal';
import { toast } from 'sonner';
import moment from 'moment';

import {
    AlertCircle,
    PackagePlus,
    Pencil,
    Plus,
    RefreshCw,
    SlidersHorizontal,
    Trash2,
    Zap,
} from 'lucide-react';

import PageHeader from '../../layouts/PageHeader';
import RuleFormModal from './RuleFormModal';
import axios from '../../../services/AxiosInstance';
import { getApiUrl } from '../../../utils/ApiUtils';
import {
    listRules,
    createRule,
    updateRule,
    deleteRule,
    toggleRule,
    installPack,
    conditionsSummary,
    actionSummary,
    STATEMENT_SOURCE_LABELS,
    SKATTEKONTO_PACK_ID,
} from '../../../services/RulesService';

/**
 * Regler — bokföringsregler for bank transactions (regelverk, L2).
 *
 * Phase D (UX redesign): lives inside Inställningar → Automatisering via
 * the `embedded` prop (modern-card chrome, no page header); the old
 * standalone /matching/rules route redirects there. The router-state
 * prefill from the "Skapa regel?" CTA works in both modes (useLocation).
 *
 * Lists the company's booking rules (user rules + installed pack
 * rules), with create/edit/delete, per-rule aktiv toggle, and the
 * "Installera Skattekonto-paket" starter-pack action. The backend may
 * not be deployed yet — every fetch failure renders an error state with
 * retry, never a crash.
 */

const formatKr = (v) => `${new Intl.NumberFormat('sv-SE').format(parseFloat(v))} kr`;

export default function RulesPage({ embedded = false }) {
    const { companyId } = useParams();
    const location = useLocation();
    const routerHistory = useHistory();

    const [rules, setRules] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [error, setError] = useState(null);

    // Chart-of-accounts options for the form's account picker. A failed
    // fetch leaves the picker empty but never blocks the page.
    const [accounts, setAccounts] = useState([]);

    const [formRule, setFormRule] = useState(null);       // rule being edited, or {} sentinel via showForm
    const [showForm, setShowForm] = useState(false);
    const [saving, setSaving] = useState(false);

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteBusy, setDeleteBusy] = useState(false);

    const [showInstallConfirm, setShowInstallConfirm] = useState(false);
    const [installBusy, setInstallBusy] = useState(false);

    // Rules with an in-flight toggle — their switch is disabled meanwhile.
    const [togglingIds, setTogglingIds] = useState(() => new Set());

    const load = useCallback(async () => {
        if (!companyId) return;
        try {
            const data = await listRules(companyId);
            setRules(Array.isArray(data?.rules) ? data.rules : []);
            setError(null);
        } catch (err) {
            setError(err?.response?.data?.message || err.message || 'Kunde inte hämta regler');
        } finally {
            setIsLoading(false);
            setIsRefreshing(false);
        }
    }, [companyId]);

    useEffect(() => {
        setIsLoading(true);
        load();
    }, [load]);

    useEffect(() => {
        if (!companyId) return;
        axios.get(getApiUrl(`${companyId}/accounting/accounts?scope=company`), { withCredentials: true })
            .then((result) => {
                const options = (result.data?.accounts || []).map((account) => ({
                    value: String(account.account_number),
                    label: `${account.account_number} - ${account.account_name}`,
                    account_number: String(account.account_number),
                    account_name: account.account_name,
                }));
                setAccounts(options);
            })
            .catch(() => {});
    }, [companyId]);

    const handleRefresh = useCallback(() => {
        setIsRefreshing(true);
        load();
    }, [load]);

    // Rule-proposal prefill (§4.5 "skapa regel?"-CTA): the Matchning page
    // navigates here with a proposalToFormRule() object in router state —
    // open the create modal seeded with it. The state is consumed
    // immediately (history.replace) so a refresh doesn't reopen the modal.
    useEffect(() => {
        const prefillRule = location.state?.prefillRule;
        if (!prefillRule) return;
        setFormRule(prefillRule); // no id → RuleFormModal runs in create mode
        setShowForm(true);
        routerHistory.replace({ pathname: location.pathname, state: undefined });
    }, [location.state, location.pathname, routerHistory]);

    const packInstalled = useMemo(
        () => rules.some((rule) => rule.source === 'pack' && rule.pack_id === SKATTEKONTO_PACK_ID),
        [rules],
    );

    const openCreate = () => { setFormRule(null); setShowForm(true); };
    const openEdit = (rule) => { setFormRule(rule); setShowForm(true); };

    const handleSave = useCallback(async (payload) => {
        setSaving(true);
        try {
            if (formRule?.id) {
                await updateRule(companyId, formRule.id, payload);
                toast.success('Regeln uppdaterades.');
            } else {
                await createRule(companyId, payload);
                toast.success('Regeln skapades.');
            }
            setShowForm(false);
            setFormRule(null);
            setIsRefreshing(true);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.message || err.message || 'Kunde inte spara regeln');
        } finally {
            setSaving(false);
        }
    }, [companyId, formRule, load]);

    const handleToggle = useCallback(async (rule) => {
        setTogglingIds((prev) => new Set(prev).add(rule.id));
        try {
            await toggleRule(companyId, rule.id);
            // Flip locally — no full refetch needed for a boolean.
            setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, active: !r.active } : r)));
        } catch (err) {
            toast.error(err?.response?.data?.message || err.message || 'Kunde inte ändra regelns status');
        } finally {
            setTogglingIds((prev) => {
                const next = new Set(prev);
                next.delete(rule.id);
                return next;
            });
        }
    }, [companyId]);

    const handleDelete = useCallback(async () => {
        if (!deleteTarget) return;
        setDeleteBusy(true);
        try {
            await deleteRule(companyId, deleteTarget.id);
            toast.success('Regeln togs bort.');
            setDeleteTarget(null);
            setIsRefreshing(true);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.message || err.message || 'Kunde inte ta bort regeln');
        } finally {
            setDeleteBusy(false);
        }
    }, [companyId, deleteTarget, load]);

    const handleInstallPack = useCallback(async () => {
        setInstallBusy(true);
        try {
            const data = await installPack(companyId, SKATTEKONTO_PACK_ID);
            const installed = data?.installed ?? data?.count;
            toast.success(installed
                ? `Skattekonto-paketet installerades — ${installed} regler tillagda.`
                : 'Skattekonto-paketet installerades.');
            setShowInstallConfirm(false);
            setIsRefreshing(true);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.message || err.message || 'Kunde inte installera paketet');
        } finally {
            setInstallBusy(false);
        }
    }, [companyId, load]);

    const headerActions = (
        <div className="d-flex gap-2 flex-wrap">
            <Button variant="outline-secondary" size="sm" onClick={handleRefresh} disabled={isRefreshing}>
                <RefreshCw size={14} className={isRefreshing ? 'me-1 spin' : 'me-1'} />
                Uppdatera
            </Button>
            {!packInstalled && (
                <Button variant="outline-primary" size="sm" onClick={() => setShowInstallConfirm(true)} disabled={installBusy}>
                    <PackagePlus size={14} className="me-1" />
                    Installera Skattekonto-paket
                </Button>
            )}
            <Button variant="primary" size="sm" onClick={openCreate}>
                <Plus size={14} className="me-1" />
                Ny regel
            </Button>
        </div>
    );

    return (
        <>
            <div
                className={embedded ? 'rules-embedded' : undefined}
                style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
            >
                {embedded ? (
                    /* Settings-card chrome (Inställningar → Automatisering) */
                    <div className="modern-card" style={{ marginBottom: 0 }}>
                        <div className="modern-card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                            <div>
                                <h2 className="section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <SlidersHorizontal size={20} />
                                    Regler
                                </h2>
                                <p className="section-subtitle">
                                    Bokföringsregler för banktransaktioner — träffar bokförs
                                    eller föreslås automatiskt på Bankhändelser.
                                </p>
                            </div>
                            {headerActions}
                        </div>
                    </div>
                ) : (
                    /* Standalone page header (legacy route) */
                    <PageHeader
                        icon={SlidersHorizontal}
                        title="Regler"
                        subtitle="Bokföringsregler för banktransaktioner."
                        backTo={{ to: `/${companyId}/bankstatements`, label: 'Till Bankhändelser' }}
                        actions={headerActions}
                    />
                )}

                {error && (
                    <Alert variant="danger" className="mb-0 d-flex align-items-center justify-content-between" style={{ fontSize: '0.875rem' }}>
                        <span>
                            <AlertCircle size={14} className="me-2" />
                            {error}
                        </span>
                        <Button size="sm" variant="outline-danger" onClick={handleRefresh}>
                            Försök igen
                        </Button>
                    </Alert>
                )}

                {isLoading ? (
                    <div className="rules-state-box" style={{ padding: 40, textAlign: 'center' }}>
                        <Spinner animation="border" size="sm" />
                        <span className="ms-2">Laddar regler…</span>
                    </div>
                ) : rules.length === 0 && !error ? (
                    <div className="rules-state-box" style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
                        <SlidersHorizontal size={32} style={{ opacity: 0.4 }} />
                        <p style={{ marginTop: 12, marginBottom: 4, fontWeight: 500 }}>Inga regler ännu</p>
                        <p style={{ fontSize: 13, marginBottom: 16 }}>
                            Regler bokför återkommande banktransaktioner åt dig — börja med
                            Skattekonto-paketet eller skapa en egen regel.
                        </p>
                        <div className="d-flex gap-2 justify-content-center">
                            <Button variant="outline-primary" size="sm" onClick={() => setShowInstallConfirm(true)}>
                                <PackagePlus size={14} className="me-1" />
                                Installera Skattekonto-paket
                            </Button>
                            <Button variant="primary" size="sm" onClick={openCreate}>
                                <Plus size={14} className="me-1" />
                                Ny regel
                            </Button>
                        </div>
                    </div>
                ) : rules.length > 0 && (
                    /* Sentence list (owner 2026-07-28: the 7-column table
                       failed the grandmother test). One rule = one plain
                       sentence + one switch. Namn/prioritet/träffar live in
                       small print and the edit modal; rails get group
                       headers only when both exist. */
                    <div className="rules-state-box">
                        {['tax_authority', 'bank']
                            .map((rail) => [rail, rules.filter((r) => (r.statement_source || 'bank') === rail)])
                            .filter(([, railRules]) => railRules.length > 0)
                            .map(([rail, railRules], railIndex, railGroups) => (
                                <React.Fragment key={rail}>
                                    {railGroups.length > 1 && (
                                        <div style={{
                                            padding: '10px 16px 6px',
                                            fontSize: '0.6875rem',
                                            fontWeight: 700,
                                            letterSpacing: '0.05em',
                                            textTransform: 'uppercase',
                                            color: '#9ca3af',
                                            borderTop: railIndex > 0 ? '1px solid #f3f4f6' : 'none',
                                        }}>
                                            {STATEMENT_SOURCE_LABELS[rail] || rail} ({railRules.length})
                                        </div>
                                    )}
                                    {railRules.map((rule) => (
                                        <div
                                            key={rule.id}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 12,
                                                padding: '10px 16px',
                                                borderBottom: '1px solid #f3f4f6',
                                                opacity: rule.active ? 1 : 0.55,
                                            }}
                                        >
                                            <Form.Check
                                                type="switch"
                                                id={`rule-active-${rule.id}`}
                                                checked={Boolean(rule.active)}
                                                disabled={togglingIds.has(rule.id)}
                                                onChange={() => handleToggle(rule)}
                                                aria-label={`Aktivera/avaktivera ${rule.name}`}
                                                title={rule.active ? 'Regeln är på' : 'Regeln är av'}
                                            />
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: '0.875rem', color: '#1f2937' }}>
                                                    {conditionsSummary(rule.conditions)}
                                                    <span style={{ color: '#9ca3af' }}> → </span>
                                                    {actionSummary(rule.action)}
                                                </div>
                                                <div style={{ fontSize: '0.6875rem', color: '#9ca3af' }}>
                                                    {rule.name}
                                                    {rule.source === 'pack' && ' · från Skattekonto-paketet'}
                                                    {(rule.hit_count ?? 0) > 0 && ` · har använts ${rule.hit_count} ${rule.hit_count === 1 ? 'gång' : 'gånger'}`}
                                                    {rule.last_hit_at && ` (senast ${moment(rule.last_hit_at).format('YYYY-MM-DD')})`}
                                                </div>
                                            </div>
                                            {rule.auto_apply && (
                                                <Badge bg="warning" text="dark" style={{ fontSize: '0.6875rem', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                                    <Zap size={10} style={{ verticalAlign: -1 }} /> Auto
                                                    {rule.amount_cap != null && ` ≤ ${formatKr(rule.amount_cap)}`}
                                                </Badge>
                                            )}
                                            <Button
                                                variant="link"
                                                className="p-1"
                                                style={{ flexShrink: 0 }}
                                                onClick={() => openEdit(rule)}
                                                aria-label={`Redigera ${rule.name}`}
                                            >
                                                <Pencil size={15} />
                                            </Button>
                                            <Button
                                                variant="link"
                                                className="p-1 text-danger"
                                                style={{ flexShrink: 0 }}
                                                onClick={() => setDeleteTarget(rule)}
                                                aria-label={`Ta bort ${rule.name}`}
                                            >
                                                <Trash2 size={15} />
                                            </Button>
                                        </div>
                                    ))}
                                </React.Fragment>
                            ))}
                    </div>
                )}
            </div>

            {/* Create/edit modal */}
            <RuleFormModal
                show={showForm}
                onHide={() => { setShowForm(false); setFormRule(null); }}
                rule={formRule}
                accounts={accounts}
                onSave={handleSave}
                saving={saving}
            />

            {/* Delete confirm */}
            <SettingsModal
                show={!!deleteTarget}
                onHide={() => !deleteBusy && setDeleteTarget(null)}
                closeButton={!deleteBusy}
                icon={Trash2}
                title="Ta bort regel"
                footer={(
                    <>
                        <Button variant="outline-secondary" onClick={() => setDeleteTarget(null)} disabled={deleteBusy}>
                            Avbryt
                        </Button>
                        <Button variant="danger" onClick={handleDelete} disabled={deleteBusy}>
                            {deleteBusy ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Tar bort…
                                </>
                            ) : (
                                <>
                                    <Trash2 size={14} className="me-1" />
                                    Ta bort regeln
                                </>
                            )}
                        </Button>
                    </>
                )}
            >
                <p className="mb-2">
                    Ta bort regeln <strong>{deleteTarget?.name}</strong>?
                </p>
                <p className="settings-modal__note mb-0">
                    Redan bokförda verifikat påverkas inte, men regeln slutar träffa nya
                    transaktioner. Detta går inte att ångra.
                </p>
            </SettingsModal>

            {/* Install-pack confirm + info */}
            <SettingsModal
                show={showInstallConfirm}
                onHide={() => !installBusy && setShowInstallConfirm(false)}
                closeButton={!installBusy}
                icon={PackagePlus}
                title="Installera Skattekonto-paket"
                footer={(
                    <>
                        <Button variant="outline-secondary" onClick={() => setShowInstallConfirm(false)} disabled={installBusy}>
                            Avbryt
                        </Button>
                        <Button variant="primary" onClick={handleInstallPack} disabled={installBusy}>
                            {installBusy ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Installerar…
                                </>
                            ) : (
                                <>
                                    <PackagePlus size={14} className="me-1" />
                                    Installera paketet
                                </>
                            )}
                        </Button>
                    </>
                )}
            >
                <p className="mb-2">
                    Paketet innehåller <strong>13+ färdiga regler</strong> för
                    skattekontotransaktioner — t.ex. inbetalningar, moms,
                    arbetsgivaravgifter och avdragen skatt.
                </p>
                <p className="settings-modal__note mb-0">
                    Reglerna installeras <strong>avstängda för auto-bokföring</strong> — de
                    föreslår bara konteringar tills du själv slår på auto-bokföring med ett
                    beloppstak per regel.
                </p>
            </SettingsModal>

            {/* Card/spinner styles from scss/components/_modern-card.scss (Phase E). */}
        </>
    );
}
